/**
 * Theme index — central export point
 * Usage: import { useTheme } from '../theme';
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';
import { useColorScheme } from '../platform/colorScheme';
import { storage } from '../platform/storage';
import { STORAGE_KEYS } from '../constants/storage';
import { lightColors, darkColors } from './colors';
import {
  fontFamily,
  fontSize,
  fontWeight,
  lineHeight,
  letterSpacing,
  textVariants,
} from './fonts';
import {
  spacing,
  borderRadius,
  iconSize,
  avatarSize,
  buttonHeight,
  inputHeight,
  zIndex,
} from './spacing';
import { shadows } from './shadows';
import type { Theme, ThemeContextValue, ThemeMode, ThemePreference } from '../types/theme';

// Build theme object from mode
const buildTheme = (mode: ThemeMode): Theme => {
  const colors = mode === 'dark' ? darkColors : lightColors;
  return {
    mode,
    isDark: mode === 'dark',
    colors,
    fonts: { fontFamily, fontSize, fontWeight, lineHeight, letterSpacing },
    textVariants,
    spacing,
    borderRadius,
    iconSize,
    avatarSize,
    buttonHeight,
    inputHeight,
    zIndex,
    shadows,
  };
};

export const lightTheme: Theme = buildTheme('light');
export const darkTheme: Theme = buildTheme('dark');

// ─── Theme Context ────────────────────────────────────────────────────────────

const ThemeContext = createContext<ThemeContextValue>({
  ...lightTheme,
  preference: 'system',
  preferenceLoaded: false,
  setPreference: () => {},
  toggleTheme: () => {},
  setMode: () => {},
});

/** Only these stored values are valid preferences. */
const isThemePreference = (value: unknown): value is ThemePreference =>
  value === 'light' || value === 'dark' || value === 'system';

export const ThemeProvider = ({ children }: PropsWithChildren): React.JSX.Element => {
  const systemScheme = useColorScheme();
  // Start from the OS scheme; the persisted preference is loaded async on mount.
  const [preference, setPreferenceState] = useState<ThemePreference>('system');
  const [preferenceLoaded, setPreferenceLoaded] = useState(false);
  const userChoseRef = useRef(false);

  // Load the persisted preference once. Storage is async on both platforms.
  useEffect(() => {
    let active = true;
    storage
      .getItem<string>(STORAGE_KEYS.THEME_MODE)
      .then(stored => {
        if (!active) return;
        // A choice made while this read was in flight is newer than what it
        // returns, so it must not be clobbered.
        if (userChoseRef.current) return;
        if (isThemePreference(stored)) setPreferenceState(stored);
      })
      .finally(() => {
        if (active) setPreferenceLoaded(true);
      });
    return () => {
      active = false;
    };
  }, []);

  const resolvedMode: ThemeMode =
    preference === 'system'
      ? systemScheme === 'dark'
        ? 'dark'
        : 'light'
      : preference;

  const theme = buildTheme(resolvedMode);

  const setPreference = (next: ThemePreference): void => {
    userChoseRef.current = true;
    setPreferenceState(next);
    setPreferenceLoaded(true);
    // Persist best-effort; storage is async and guards its own failures.
    storage.setItem(STORAGE_KEYS.THEME_MODE, next);
  };

  const setMode = (mode: ThemeMode): void => setPreference(mode);

  const toggleTheme = (): void =>
    setPreference(resolvedMode === 'light' ? 'dark' : 'light');

  const value: ThemeContextValue = {
    ...theme,
    preference,
    preferenceLoaded,
    setPreference,
    setMode,
    toggleTheme,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): ThemeContextValue => useContext(ThemeContext);

export type { ThemePreference } from '../types/theme';

export default buildTheme;
